import { useSelector } from 'react-redux';

export const usePermission = () => {
  const userPermissions = useSelector((state) => state.auth.authUser?.permissions);

  const evaluate = (permissions = ['']) => {
    return Array.isArray(permissions) && permissions.every((permission) => userPermissions?.includes(permission));
  };

  return { evaluate };
};

export const permissionList = {
  adjustmentFeeReportRead: 'adjustment-fee-report:read',
  amountTransactionRead: 'amount-transaction:read',
  assignMerchantUpdate: 'assign merchant:update',
  auditCreate: 'audit:create',
  auditRead: 'audit:read',
  auto2FaRemove: 'auth2fa:remove',
  bankCreate: 'bank:create',
  bankDelete: 'bank:delete',
  bankExport: 'bank:export',
  bankOverdraft: 'bank:overdraft',
  bankPaymentMethodCreate: 'bank-payment-method:create',
  bankPaymentMethodDelete: 'bank-payment-method:delete',
  bankPaymentMethodUpdate: 'bank-payment-method:update',
  bankRead: 'bank:read',
  bankUpdate: 'bank:update',
  bankWalletMovementCreate: 'bank-wallet-movement:create',
  bankWalletMovementExport: 'bank-wallet-movement:export',
  bankWalletMovementRead: 'bank-wallet-movement:read',
  bankWalletReset: 'bank-wallet:reset',
  bankWalletUpdateDiscrepancy: 'bank-wallet:update-discrepancy',
  bankCashoutCreate: 'bank-cashout:create',
  bankCashoutRead: 'bank-cashout:read',
  bankCashoutUpload: 'bank-cashout:upload',
  bankCashoutExport: 'bank-cashout:export',
  bankCbuCreate: 'bank-cbu:create',
  bankCbuDelete: 'bank-cbu:delete',
  bankCbuRead: 'bank-cbu:read',
  bankCbuUpdate: 'bank-cbu:update',
  updateFees: 'transaction:update-fees',
  countTransactionsBanksRead: 'count-transactions-banks:read',
  currencyCreate: 'currency:create',
  currencyDelete: 'currency:delete',
  currencyRead: 'currency:read',
  currencyUpdate: 'currency:update',
  customerBankCreate: 'customer-bank:create',
  customerBankDelete: 'customer-bank:delete',
  customerBankRead: 'customer-bank:read',
  customerBankUpdate: 'customer-bank:update',
  customerDocumentCreate: 'customer-document:create',
  customerDocumentExport: 'customer-document:export',
  customerDocumentRead: 'customer-document:read',
  customerExport: 'customer:export',
  customerExternalData: 'customer:external-data',
  customerRead: 'customer:read',
  customerRemovePep: 'customer:remove-pep',
  customerUpdate: 'customer:update',
  customerUpdateAmlStatus: 'customer:update-aml-status',
  dailyTransactionsRead: 'daily-transactions:read',
  dashboardExchange: 'dashboard:exchange',
  documentApprove: 'document:approve',
  documentCreate: 'document:create',
  documentRefuse: 'document:refuse',
  global: 'global',
  globalAdmin: 'global-admin',
  healthRead: 'health:read',
  kycManualRequest: 'kyc:manual-request',
  kycUpdateStatus: 'kyc:update-status',
  loginAdmin: 'login:admin',
  manualTransaction: 'bank:manual-transaction',
  menuBalances: 'menu:balances',
  menuBanks: 'menu:banks',
  menuDashboard: 'menu:dashboard',
  menuMerchants: 'menu:merchants',
  merchantCreate: 'merchant:create',
  merchantDelete: 'merchant:delete',
  merchantExport: 'merchant:export',
  merchantListOrder: 'merchant:list-order',
  merchantMonthlyReportsDownload: 'merchant-monthly-report:download',
  merchantMonthlyReportsRead: 'merchant-monthly-report:read',
  merchantMonthlyReportsExport: 'merchant-monthly-report:export',
  merchantDailyReportsDownload: 'merchant-daily-report:download',
  merchantDailyReportsRead: 'merchant-daily-report:read',
  merchantDailyReportsExport: 'merchant-daily-report:export',
  merchantRead: 'merchant:read',
  merchantSecretUpdate: 'merchant-secret:update',
  settlementRead: 'settlement:read',
  settlementCreate: 'settlement:create',
  settlementDelete: 'settlement:delete',
  settlementReadOwn: 'settlement:read-own',
  settlementCreateOwn: 'settlement:create-own',
  settlementApprove: 'settlement:approve',
  settlementTransactionRead: 'settlement-transaction:read',
  settlementTransactionMerchantUpdate: 'settlement-transaction-merchant:update',
  settlementTransactionUpdate: 'settlement-transaction:update',
  merchantWebhookSecretUpdate: 'merchant-webhook-secret:update',
  merchantUpdate: 'merchant:update',
  conversionMonitorRead: 'conversion-monitor:read',
  conversionMonitorDelete: 'conversion-monitor:delete',
  conversionMonitorWrite: 'conversion-monitor:write',
  notesCreate: 'notes:create',
  notesDelete: 'notes:delete',
  notesRead: 'notes:read',
  notesUpdate: 'notes:update',
  notificationExport: 'notification:export',
  notificationRead: 'notification:read',
  oAuthClientRead: 'oauth-client:read',
  passwordUpdate: 'password:update',
  paymentMethodCreate: 'payment-method:create',
  paymentMethodDelete: 'payment-method:delete',
  paymentMethodUpdate: 'payment-method:update',
  pixAndGoRead: 'pix-and-go:read',
  pixAndGoSimulate: 'pix-and-go:simulate-payment',
  pepCreate: 'pep:create',
  pepDelete: 'pep:delete',
  pepRead: 'pep:read',
  pepUpdate: 'pep:update',
  profileRead: 'profile:read',
  profitRead: 'profit:read',
  bankfeeRead: 'bankfee:read',
  providerRequestRead: 'provider-request:read',
  providerRequestWrite: 'provider-request:write',
  relatedPepApprove: 'related-pep:approve',
  relatedPepCreate: 'related-pep:create',
  relatedPepDelete: 'related-pep:delete',
  relatedPepRead: 'related-pep:read',
  relatedPepRemove: 'related-pep:remove',
  relatedPepUpdate: 'related-pep:update',
  simulateTransactionCreate: 'simulate-transaction:create',
  summaryDailyTransactionsExport: 'summary-daily-transactions:export',
  summaryDailyTransactionsRead: 'summary-daily-transactions:read',
  dailyReportUpdate: 'daily-report:update',
  summaryPendingWithdrawal: 'summary-pending-withdrawals:read',
  summaryApprovedWithdrawal: 'summary-approved-withdrawals:read',
  summaryTransactionsCustomerExport: 'summary-transactions-customer:export',
  summaryTransactionsCustomerRead: 'summary-transactions-customer:read',
  superSetRead: 'superset:read',
  transactionBankInfoRead: 'transaction-bank-info:read',
  transactionsAdjustment: 'transaction:adjustment',
  transactionsSimulate: 'transaction:simulate',
  transactionApprove: 'transaction:approve',
  transactionTrusted: 'transaction:trusted',
  transactionCallbackFailedDelete: 'transactions-callback-failed:delete',
  transactionCallbackFailedRead: 'transactions-callback-failed:read',
  transactionCancelReasonRead: 'transactions-cancel-reason:red',
  transactionBatchComplete: 'transaction:batch-complete',
  transactionExport: 'transaction:export',
  transactionExportOffline: 'transaction:export-offline',
  transactionExternalExport: 'transaction-external:export',
  transactionExternalPeriodConciliation: 'transaction-external:period-conciliation',
  transactionFlag: 'transaction:flag',
  transactionManualCancel: 'transaction:manual-cancel',
  transactionManualComplete: 'transaction:manual-complete',
  transactionManualRefund: 'transaction:manual-refund',
  transactionManualRefundBank: 'transaction:manual-refund-bank',
  transactionManualRetry: 'transaction:manual-retry',
  transactionManualUpdateAmount: 'transaction:manual-update-amount',
  transactionReversalRetry: 'transaction:reversal-retry',
  transactionPspCheck: 'transaction:psp-check',
  transactionResendCallback: 'transaction:resend-callback',
  transactionResendCallbackMultiple: 'transaction:resend-callback-multiple',
  transactionPendingReviewRead: 'transaction-pending-review:read',
  transactionIssuesDetails: 'transaction-issue:details',
  transactionIssuesCreate: 'transaction-issue:create',
  transactionIssuesDelete: 'transaction-issue:delete',
  transactionIssueRead: 'transaction-issue:read',
  transactionIssueExport: 'transaction-issue:export',
  transactionIssueUpdate: 'transaction-issue:update',
  transactionRead: 'transaction:read',
  transactionExternalRead: 'transaction-external:read',
  transactionExternalConciliate: 'transaction-external:conciliate',
  transactionExternalRefund: 'transaction-external:refund',
  transactionErrorsRead: 'transaction-errors:read',
  transactionErrorsExport: 'transaction-errors:export',
  transactionRefuse: 'transaction:refuse',
  transactionReviewExport: 'transaction-review:export',
  transactionReviewRead: 'transaction-review:read',
  updateWallet: 'update:wallet',
  userCreate: 'user:create',
  userExport: 'user:export',
  userMerchantCreate: 'user-merchant:create',
  userMerchantCreateOwn: 'user-merchant-own:create',
  userMerchantExport: 'user-merchant:export',
  userMerchantRead: 'user-merchant:read',
  userMerchantReadOwn: 'user-merchant-own:read',
  userMerchantUpdate: 'user-merchant:update',
  userRead: 'user:read',
  userUpdate: 'user:update',
  walletBankCreate: 'wallet-bank:create',
  walletBankDelete: 'wallet-bank:delete',
  walletBankRead: 'wallet-bank:read',
  walletBankUpdate: 'wallet-bank:update',
  walletCreate: 'wallet:create',
  walletDelete: 'wallet:delete',
  walletRead: 'wallet:read',
  menuBalanceHistory: 'menu:balance-history',
  walletUpdate: 'wallet:update',
  walletExport: 'wallet:export',
};
